// services/admins.service.ts
import { AxiosResponse } from 'axios';
import { http } from './http.service';
import { translateError, translateErrorObject } from '../utils/error-translations';
import { Pagination } from '../interfaces/pagination';

// ---
export interface AdminAccount {
  id: number;
  username: string;
}

// ---
export interface AdminListItem {
  id: number;
  username: string;
  created?: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email?: string;
  phoneNumber?: string;
}

// ---
export interface AdminDetails {
  id: number;
  username: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
}

// ---
export type SortDirection = 0 | 1;

// ---
export interface AdminsListResponse extends Pagination<AdminListItem> { }

export interface LoginResponse {
  success: true;
  user: AdminAccount;
  token: string;
}

export interface LoginError {
  success: false;
  error: string;
  field?: 'username' | 'password';
}

export interface RegisterResponse extends AdminAccount {}

export interface DashboardProfessionSummary {
  professionId: number;
  professionName: string;
  professionalsCount: number;
}

export interface DashboardSummary {
  projectsCount: number;
  usersCount: number;
  adminsCount: number;
  professionProfessionals: DashboardProfessionSummary[];
}

interface LoginPayload {
  username: string;
  password: string;
  rememberMe?: boolean;
}

interface RegisterPayload {
  username: string;
  password: string;
}

interface UpdateAdminPayload {
  adminId: number;
  username?: string | null;
  password?: string | null;
}

interface RegisterOptions {
  authorizationToken?: string;
}

interface AdminsServiceType {
  login(username: string, password: string): Promise<LoginResponse>;
  logout(): Promise<void>;
  checkSession(): Promise<boolean>;
  deleteSession(): Promise<void>;
  register(payload: RegisterPayload, options?: RegisterOptions): Promise<RegisterResponse>;
  createAdmin(payload: RegisterPayload, options?: RegisterOptions): Promise<RegisterResponse>;
  updateAdmin(payload: UpdateAdminPayload): Promise<void>;
  getCurrentAdmin(): Promise<AdminAccount | null>;
  getAdmin(adminId: string | number): Promise<AdminDetails>;
  deleteAdmin(adminId: string | number): Promise<void>;
  getDashboardSummary(): Promise<DashboardSummary>;
  getAllAdmins(): Promise<AdminsListResponse>;
}

const DEFAULT_LOGIN_ERROR: LoginError = {
  success: false,
  error: 'שגיאה בהתחברות',
  field: 'username'
};

const DEFAULT_REGISTER_ERROR = 'שגיאה ברישום מנהל';
const DEFAULT_UPDATE_ADMIN_ERROR = 'שגיאה בעדכון מנהל';
const DEFAULT_GET_ADMIN_ERROR = 'שגיאה בטעינת פרטי מנהל';
const DEFAULT_DELETE_ADMIN_ERROR = 'שגיאה במחיקת מנהל';
const DEFAULT_DASHBOARD_SUMMARY_ERROR = 'שגיאה בטעינת נתוני לוח הבקרה';
const DEFAULT_FETCH_ADMINS_ERROR = 'שגיאה בטעינת רשימת מנהלי המערכת';

type ErrorResponseShape = {
  message?: string;
  error?: string;
  errors?: Record<string, unknown>;
  [key: string]: unknown;
};

interface DashboardProfessionSummaryApiItem {
  profession?: number;
  professionalsCount?: number;
}

interface DashboardSummaryApiResponse {
  projectsCount?: number;
  totalUsersCount?: number;
  adminsCount?: number;
  professionSummaries?: DashboardProfessionSummaryApiItem[];
  // Legacy fields
  usersCount?: number;
  professionProfessionals?: any[];
}

type DashboardSummaryResponse = {
  data?: DashboardSummaryApiResponse;
} & Partial<DashboardSummaryApiResponse>; // For backward compatibility if flat structure persists

interface AdminsApiItem {
  id?: number | string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phoneNumber?: string | null;
  username?: string | null;
  created?: string | null;
}

interface AdminsApiResponse {
  items?: AdminsApiItem[];
  data?: AdminsApiItem[];
}



const mapErrorField = (fieldKey?: string | null): LoginError['field'] | undefined => {
  if (!fieldKey) return undefined;
  const normalized = fieldKey.trim().toLowerCase();
  if (!normalized) return undefined;
  if (normalized === 'username' || normalized.includes('user')) return 'username';
  if (normalized === 'password' || normalized.includes('pass')) return 'password';
  return undefined;
};

const parseApiErrorDetails = (data?: ErrorResponseShape | string): { message?: string; field?: LoginError['field'] } => {
  if (!data) return {};
  if (typeof data === 'string') {
    const translated = translateError(data);
    return translated ? { message: translated } : {};
  }

  // Handle new API response structure with "meta"
  const rawData = data as any;
  if (rawData.meta && typeof rawData.meta === 'object') {
    const { description, status, title } = rawData.meta;
    
    // A wrong password and an unknown username both answer 400
    // INVALID_CREDENTIALS (an unknown username used to answer 404), so one
    // message covers both.
    if (description === 'INVALID_CREDENTIALS' || status === 404 || title === 'NotFound' || description === 'No elements found.') {
      return { message: 'שם המשתמש או הסיסמה שגויים', field: 'password' };
    }

    // 10 failed attempts for the username, or 20 from the network, in 15 minutes.
    if (description === 'TOO_MANY_ATTEMPTS' || status === 429) {
      return { message: 'יותר מדי ניסיונות התחברות. נסו שוב מאוחר יותר.', field: 'password' };
    }

    if (description) {
      return { message: translateError(description) || description };
    }
  }

  if (data.errors && typeof data.errors === 'object') {
    const errors = data.errors as Record<string, unknown>;
    const aggregatedMessage = translateErrorObject(errors);
    let mappedField: LoginError['field'] | undefined;

    for (const key of Object.keys(errors)) {
      mappedField = mapErrorField(key);
      if (mappedField) break;
    }

    return {
      message: aggregatedMessage || undefined,
      field: mappedField
    };
  }
  if (data.message) return { message: translateError(data.message) };
  if (data.error) return { message: translateError(data.error) };
  return {};
};

const checkIsAxiosError = (error: unknown): error is { response?: { data?: ErrorResponseShape | string; status?: number } } => {
    return !!(error && typeof error === 'object' && 'response' in error);
};

const normalizeLoginError = (error: unknown): LoginError => {
  if (checkIsAxiosError(error)) {
    const parsed = parseApiErrorDetails(error.response?.data);
    const message = parsed.message || DEFAULT_LOGIN_ERROR.error;
    const status = error.response?.status;
    return {
      success: false,
      error: message,
      field: parsed.field || (status === 401 ? 'password' : DEFAULT_LOGIN_ERROR.field)
    };
  }
  if (error && typeof error === 'object' && 'error' in (error as Record<string, unknown>)) {
    const raw = error as LoginError;
    return {
      success: false,
      error: raw.error || DEFAULT_LOGIN_ERROR.error,
      field: raw.field || DEFAULT_LOGIN_ERROR.field
    };
  }
  return DEFAULT_LOGIN_ERROR;
};

const normalizeRegisterError = (error: unknown): Error => {
  if (checkIsAxiosError(error)) {
    const parsed = parseApiErrorDetails(error.response?.data);
    return new Error(parsed.message || DEFAULT_REGISTER_ERROR);
  }
  if (error instanceof Error) return error;
  return new Error(DEFAULT_REGISTER_ERROR);
};

const coerceNonNegativeInteger = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value < 0 ? 0 : Math.round(value);
  }
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed < 0 ? 0 : Math.round(parsed);
    }
  }
  return 0;
};

const normalizeProfessionProfessionals = (
  items?: Array<Partial<DashboardProfessionSummary>> | DashboardProfessionSummaryApiItem[] | null
): DashboardProfessionSummary[] => {
  if (!Array.isArray(items)) return [];
  return items.map((entry) => {
    // Handle both old and new structure
    const anyEntry = entry as any;
    const professionId = anyEntry.professionId ?? anyEntry.profession;
    
    return {
      professionId: coerceNonNegativeInteger(professionId),
      professionName: typeof anyEntry.professionName === 'string' ? anyEntry.professionName : '',
      professionalsCount: coerceNonNegativeInteger(anyEntry.professionalsCount)
    };
  });
};

const normalizeDashboardSummary = (payload?: DashboardSummaryResponse | null): DashboardSummary => {
  const root = payload ?? {};
  // Check for nested data structure or flat structure
  const data = (root.data ?? root) as DashboardSummaryApiResponse;

  return {
    projectsCount: coerceNonNegativeInteger(data.projectsCount),
    usersCount: coerceNonNegativeInteger(data.totalUsersCount ?? data.usersCount), // Map totalUsersCount -> usersCount
    adminsCount: coerceNonNegativeInteger(data.adminsCount),
    professionProfessionals: normalizeProfessionProfessionals(data.professionSummaries ?? data.professionProfessionals) // Map professionSummaries -> professionProfessionals
  };
};

const normalizeDashboardSummaryError = (error: unknown): Error => {
  if (checkIsAxiosError(error)) {
    const parsed = parseApiErrorDetails(error.response?.data);
    return new Error(parsed.message || DEFAULT_DASHBOARD_SUMMARY_ERROR);
  }
  if (error instanceof Error) return error;
  return new Error(DEFAULT_DASHBOARD_SUMMARY_ERROR);
};

const normalizeAdminsListError = (error: unknown): Error => {
  if (checkIsAxiosError(error)) {
    const parsed = parseApiErrorDetails(error.response?.data);
    return new Error(parsed.message || DEFAULT_FETCH_ADMINS_ERROR);
  }
  if (error instanceof Error) return error;
  return new Error(DEFAULT_FETCH_ADMINS_ERROR);
};

const normalizeUpdateAdminError = (error: unknown): Error => {
  if (checkIsAxiosError(error)) {
    const parsed = parseApiErrorDetails(error.response?.data);
    return new Error(parsed.message || DEFAULT_UPDATE_ADMIN_ERROR);
  }
  if (error instanceof Error) return error;
  return new Error(DEFAULT_UPDATE_ADMIN_ERROR);
};

const normalizeGetAdminError = (error: unknown): Error => {
  if (checkIsAxiosError(error)) {
    const parsed = parseApiErrorDetails(error.response?.data);
    return new Error(parsed.message || DEFAULT_GET_ADMIN_ERROR);
  }
  if (error instanceof Error) return error;
  return new Error(DEFAULT_GET_ADMIN_ERROR);
};

const normalizeDeleteAdminError = (error: unknown): Error => {
  if (checkIsAxiosError(error)) {
    const parsed = parseApiErrorDetails(error.response?.data);
    return new Error(parsed.message || DEFAULT_DELETE_ADMIN_ERROR);
  }
  if (error instanceof Error) return error;
  return new Error(DEFAULT_DELETE_ADMIN_ERROR);
};


const normalizeAdminAccountPayload = (raw: unknown): AdminAccount | null => {
  const data = (raw ?? {}) as Record<string, unknown>;
  const normalizedId = Number(data.id);
  const normalizedUsername = typeof data.username === 'string' ? (data.username as string).trim() : '';
  if (!Number.isFinite(normalizedId) || !normalizedUsername) {
    return null;
  }
  return {
    id: normalizedId,
    username: normalizedUsername
  };
};

const executeAdminRegistration = async (
  payload: RegisterPayload,
  options: RegisterOptions = {}
): Promise<RegisterResponse> => {
  const headers = options.authorizationToken
    ? { Authorization: options.authorizationToken }
    : undefined;

  // Endpoint corrected to POST /Admins (was /admins/register)
  const data = await http.post<AdminAccount>(
    '/Admins',
    payload,
    {
      headers,
      // The admin form shows the validation errors next to its fields.
      silentErrors: true
    }
  );

  // If data is just a status string or null (from a 200 OK without body), we might need to handle it.
  // The screenshot says 200 OK "If the request has succeeded".
  // It does NOT show a response body for 200.
  // So we mock a return if body is empty but status was 200.
  
  if (!data) {
    // Return a mock response since we don't get the created admin back
    return {
        id: 0,
        username: payload.username
    };
  }

  const normalized = normalizeAdminAccountPayload(data);
  // If normalization failed but we got *something* (maybe empty object), fall back to input
  if (!normalized) {
     return {
        id: 0,
        username: payload.username
    };
  }

  return normalized;
};

const normalizeAdminItem = (item: AdminsApiItem): AdminListItem => {
  const firstName = typeof item.firstName === 'string' ? item.firstName.trim() : '';
  const lastName = typeof item.lastName === 'string' ? item.lastName.trim() : '';
  const email = typeof item.email === 'string' ? item.email.trim() : undefined;
  const phoneNumber = typeof item.phoneNumber === 'string' ? item.phoneNumber.trim() : undefined;
  const username = typeof item.username === 'string' ? item.username.trim() : '';
  const created = typeof item.created === 'string' ? item.created : undefined;

  const idCandidates = [item.id];
  let normalizedId = 0;
  for (const candidate of idCandidates) {
    if (typeof candidate === 'number' && Number.isFinite(candidate)) {
      normalizedId = candidate;
      break;
    }
    if (typeof candidate === 'string' && candidate.trim()) {
      const parsed = Number(candidate);
      if (Number.isFinite(parsed)) {
        normalizedId = parsed;
        break;
      }
    }
  }
  
  // Make sure we prefer explicit names if available, else username
  let fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
  if (!fullName) {
      if (username) fullName = username;
      else if (email) fullName = email;
      else if (phoneNumber) fullName = phoneNumber;
      else fullName = 'מנהל מערכת';
  }

  return {
    id: normalizedId,
    username,
    created,
    firstName,
    lastName,
    fullName,
    email,
    phoneNumber
  };
};

const mapAdminsListResponse = (payload: AdminsApiResponse | undefined): AdminsListResponse => {
  const rawItems = payload?.data ?? payload?.items ?? [];
  const allItems = rawItems.map(normalizeAdminItem);

  const totalCount = allItems.length;

  return {
    items: allItems,
    pageNumber: 1, 
    pageSize: totalCount,
    totalCount,
    totalPages: 1,
    hasNextPage: false,
    hasPreviousPage: false
  };
};

const fetchAdminsList = async (): Promise<AdminsListResponse> => {
  // No params sent to backend
  const params = {};

  // Remove pagination params to fetch all admins for client-side pagination
  const payload = await http.get<AdminsApiResponse>('/Admins', { params });
  return mapAdminsListResponse(payload);
};

const AdminsService: AdminsServiceType = {
  async login(username: string, password: string): Promise<LoginResponse> {
    const payload: LoginPayload = {
      username,
      password
    };

    try {
      const response = await http.post<AdminAccount>(
        '/Admins/sessions',
        payload,
        // The login form shows its own errors (wrong credentials, 429).
        { getFullResponse: true, silentErrors: true }
      ) as AxiosResponse<AdminAccount>;

      const data = response.data;

      const raw = (data ?? {}) as Record<string, unknown>;
      
      let normalizedId = Number(raw.id);
      let normalizedUsername = typeof raw.username === 'string' ? (raw.username as string).trim() : '';

      // Handle nested response structure: { data: { admin: { id, username, ... }, token: ... } }
      if ((!normalizedId || !normalizedUsername) && raw.data && typeof raw.data === 'object') {
        const innerData = raw.data as Record<string, any>;
        if (innerData.admin) {
          const adminId = Number(innerData.admin.id);
          const adminUsername = typeof innerData.admin.username === 'string' ? innerData.admin.username : '';
          
          if (Number.isFinite(adminId) && adminUsername) {
            normalizedId = adminId;
            normalizedUsername = adminUsername;
          }
        }
      }

      if (!Number.isFinite(normalizedId) || !normalizedUsername) {
        throw DEFAULT_LOGIN_ERROR;
      }

      // Extract token from response body
      let token: string | null = null;
      if (typeof window !== 'undefined') {
        
        if (typeof raw.token === 'string') {
          token = raw.token;
        } else if (typeof raw.accessToken === 'string') {
          token = raw.accessToken;
        } else if (raw.data && typeof (raw.data as any).token === 'string') {
          token = (raw.data as any).token;
        }
      }

      if (!token) {
        throw new Error('חסר טוקן');
      }

      return {
        success: true,
        user: {
          id: normalizedId,
          username: normalizedUsername
        },
        token
      };
    } catch (error) {
      throw normalizeLoginError(error);
    }
  },

  async logout(): Promise<void> {
    let requestConfig = {};
    if (typeof window !== 'undefined') {
      try {
        const authUserRaw = localStorage.getItem('authUser');
        if (authUserRaw) {
          const parsed = JSON.parse(authUserRaw);
          if (parsed && parsed.token) {
            requestConfig = {
              headers: { Authorization: parsed.token }
            };
          }
        }
      } catch (e) {
        // Ignore parsing errors
      }
    }

    try {
      await http.delete('/Admins/sessions', null, requestConfig);
    } catch (error) {
      // Ignore errors during logout (e.g. 401 if already expired)
      console.warn('Logout API warning:', error);
    } finally {
      if (typeof window !== 'undefined') {
        localStorage.removeItem('authUser');
      }
    }
  },

  async checkSession(): Promise<boolean> {
    try {
      await http.get('/Admins/sessions');
      return true;
    } catch (error) {
      // Check for 401 from Axios or custom isUnauthorized object
      if (
        (checkIsAxiosError(error) && error.response?.status === 401) ||
        (error && typeof error === 'object' && 'isUnauthorized' in (error as any))
      ) {
        return false;
      }
      return false;
    }
  },

  async deleteSession(): Promise<void> {
    await http.delete('/Admins/sessions');
  },

  async register(payload: RegisterPayload, options: RegisterOptions = {}): Promise<RegisterResponse> {
    try {
      return await executeAdminRegistration(payload, options);
    } catch (error) {
      if (error && typeof error === 'object' && 'isUnauthorized' in (error as Record<string, unknown>)) {
        throw error;
      }
      throw normalizeRegisterError(error);
    }
  },

  async createAdmin(payload: RegisterPayload, options: RegisterOptions = {}): Promise<RegisterResponse> {
    try {
      return await executeAdminRegistration(payload, options);
    } catch (error) {
      if (error && typeof error === 'object' && 'isUnauthorized' in (error as Record<string, unknown>)) {
        throw error;
      }
      if (checkIsAxiosError(error)) {
        throw error;
      }
      throw normalizeRegisterError(error);
    }
  },

  // Changing an admin's username or password ends that admin's sessions — an
  // admin who edits themself must be sent to the login screen after saving.
  async updateAdmin(payload: UpdateAdminPayload): Promise<void> {
    try {
      // The admin form shows the validation errors (INVALID_USERNAME, INVALID_PASSWORD) next to its fields.
      await http.put('/Admins', payload, { silentErrors: true });
    } catch (error) {
      if (error && typeof error === 'object' && 'isUnauthorized' in (error as Record<string, unknown>)) {
        throw error;
      }
      // Kept as is so the form can read the field errors and meta.description.
      if (checkIsAxiosError(error)) {
        throw error;
      }
      throw normalizeUpdateAdminError(error);
    }
  },

  // The logged-in admin (GET /Admins/sessions answers with it).
  async getCurrentAdmin(): Promise<AdminAccount | null> {
    try {
      const data = await http.get<any>('/Admins/sessions');
      return normalizeAdminAccountPayload(data?.data ?? data);
    } catch (error) {
      if (error && typeof error === 'object' && 'isUnauthorized' in (error as Record<string, unknown>)) {
        throw error;
      }
      return null;
    }
  },

  async getAdmin(adminId: string | number): Promise<AdminDetails> {
    try {
      const data = await http.get<AdminDetails>(`/Admins/${adminId}`);
      return data;
    } catch (error) {
      if (error && typeof error === 'object' && 'isUnauthorized' in (error as Record<string, unknown>)) {
        throw error;
      }
      throw normalizeGetAdminError(error);
    }
  },

  async deleteAdmin(adminId: string | number): Promise<void> {
    try {
      // The admins screen shows the reason (e.g. UNABLE_TO_DELETE_SELF) itself.
      await http.delete(`/Admins/${adminId}`, null, { silentErrors: true });
    } catch (error) {
      if (error && typeof error === 'object' && 'isUnauthorized' in (error as Record<string, unknown>)) {
        throw error;
      }
      throw normalizeDeleteAdminError(error);
    }
  },

  async getDashboardSummary(): Promise<DashboardSummary> {
    try {
      const data = await http.get<DashboardSummaryResponse>('/Admins/dashboard');
      return normalizeDashboardSummary(data);
    } catch (error) {
      throw normalizeDashboardSummaryError(error);
    }
  },

  async getAllAdmins(): Promise<AdminsListResponse> {
    try {
      return await fetchAdminsList();
    } catch (error) {
      if (error && typeof error === 'object' && 'isUnauthorized' in (error as Record<string, unknown>)) {
        throw error;
      }
      throw normalizeAdminsListError(error);
    }
  }
};

export default AdminsService;